/**
 * 릴리스 등록 / 수정 모달.
 * - 새 릴리스: initial=undefined → POST /releases
 * - 수정: initial 채워진 release 전달 → PATCH /releases/:id
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createRelease,
  updateRelease,
  type Release,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

const SEMVER_REGEX = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (release: Release) => void;
  initial?: Release;
}

export function ReleaseFormDialog({
  open,
  onClose,
  onSaved,
  initial,
}: Props): JSX.Element | null {
  const isEdit = Boolean(initial);
  const [version, setVersion] = useState("");
  const [channel, setChannel] = useState("stable");
  const [notes, setNotes] = useState("");
  const [macArm64Url, setMacArm64Url] = useState("");
  const [macX64Url, setMacX64Url] = useState("");
  const [windowsUrl, setWindowsUrl] = useState("");
  const [published, setPublished] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setVersion(initial?.version ?? "");
    setChannel(initial?.channel ?? "stable");
    setNotes(initial?.notes ?? "");
    setMacArm64Url(initial?.macArm64Url ?? "");
    setMacX64Url(initial?.macX64Url ?? "");
    setWindowsUrl(initial?.windowsUrl ?? "");
    setPublished(initial?.published ?? true);
    setError(null);
    setSubmitting(false);
    setTimeout(() => versionRef.current?.focus(), 0);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!isEdit && !SEMVER_REGEX.test(version)) {
      setError("version 은 SemVer (예: 0.1.0) 형식이어야 합니다.");
      return;
    }
    if (!macArm64Url && !macX64Url && !windowsUrl) {
      setError("최소 한 OS 의 다운로드 URL 은 등록해야 합니다.");
      return;
    }
    setSubmitting(true);
    try {
      const saved = isEdit
        ? await updateRelease(initial!.id, {
            channel,
            notes: notes.trim() || null,
            macArm64Url: macArm64Url.trim() || null,
            macX64Url: macX64Url.trim() || null,
            windowsUrl: windowsUrl.trim() || null,
            published,
          })
        : await createRelease({
            version,
            channel,
            ...(notes.trim() ? { notes: notes.trim() } : {}),
            ...(macArm64Url.trim() ? { macArm64Url: macArm64Url.trim() } : {}),
            ...(macX64Url.trim() ? { macX64Url: macX64Url.trim() } : {}),
            ...(windowsUrl.trim() ? { windowsUrl: windowsUrl.trim() } : {}),
            published,
          });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "저장에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {isEdit ? "릴리스 수정" : "새 릴리스 등록"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              빌드된 .dmg / .exe 의 다운로드 URL 을 입력하세요. 최소 한 OS 는
              필수입니다.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rel-version" className="text-sm font-medium">
                Version
              </label>
              <Input
                id="rel-version"
                ref={versionRef}
                type="text"
                required
                disabled={isEdit}
                placeholder="0.1.0"
                className="mt-1.5 font-mono"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              {isEdit && (
                <p className="mt-1 text-xs text-muted-foreground">
                  버전은 등록 후 변경 불가
                </p>
              )}
            </div>
            <div>
              <label htmlFor="rel-channel" className="text-sm font-medium">
                Channel
              </label>
              <Input
                id="rel-channel"
                type="text"
                placeholder="stable"
                className="mt-1.5"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="rel-notes" className="text-sm font-medium">
              릴리스 노트 <span className="text-muted-foreground">(markdown, 선택)</span>
            </label>
            <textarea
              id="rel-notes"
              className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              rows={4}
              placeholder="### 새 기능\n- ..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <UrlField
            id="rel-mac-arm"
            label="macOS Apple Silicon (.dmg)"
            value={macArm64Url}
            onChange={setMacArm64Url}
          />
          <UrlField
            id="rel-mac-x64"
            label="macOS Intel (.dmg)"
            value={macX64Url}
            onChange={setMacX64Url}
          />
          <UrlField
            id="rel-win"
            label="Windows (.exe)"
            value={windowsUrl}
            onChange={setWindowsUrl}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="accent-primary"
            />
            다운로드 페이지에 공개
          </label>
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "저장" : "등록"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UrlField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium">
        {label} <span className="text-muted-foreground">(URL)</span>
      </label>
      <Input
        id={id}
        type="url"
        placeholder="https://pub-xxx.r2.dev/v0.1.0/Devit-0.1.0-arm64.dmg"
        className="mt-1.5 font-mono text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
